import { useRef, useState } from 'react';

import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useClickOutside } from 'hooks/useClickOutside';
import { useTranslation } from 'react-i18next';

import {
  Button,
  LoadingIndicator,
  TextFieldType,
  Typography,
} from '@cfa/react-components';

import { cn } from '@/utils/tailwind';

import Constants from '@/constants';

import Searchbar from 'components/Searchbar/Searchbar';

import { getCategoryIconsOptions } from '@/_generated/api-clients/content';

interface AddIconProps {
  onAddIcon: (name: string) => void;
  onClose: () => void;
  className?: string;
}

export const AddIcon: React.FC<AddIconProps> = ({
  onAddIcon,
  onClose,
  className,
}) => {
  const pageOffset = 15;
  const [sliceAmount, setSliceAmount] = useState({
    start: 0,
    end: pageOffset,
  });
  const [iconsSearchTermDisplayed, setIconsSearchTermDisplayed] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [selectedIconName, setSelectedIconName] = useState<string | null>(null);
  const { t } = useTranslation();
  const addIconRef = useRef<HTMLDivElement>(null);
  useClickOutside(addIconRef, onClose);

  const { data: icons, isLoading: isLoadingIcons } = useQuery(
    getCategoryIconsOptions(),
  );

  const filteredIcons = icons?.filter(
    name =>
      !appliedSearchTerm ||
      name.toLowerCase().startsWith(appliedSearchTerm.toLowerCase()),
  );

  const onIconClick = (name: string) => {
    setSelectedIconName(name);
  };

  const onNext = () => {
    if (filteredIcons && sliceAmount.end >= filteredIcons.length) {
      return;
    }
    setSliceAmount(prev => ({
      start: prev.start + pageOffset,
      end: prev.end + pageOffset,
    }));
  };

  const onPrevious = () => {
    if (sliceAmount.start === 0) {
      return;
    }
    setSliceAmount(prev => ({
      start: prev.start - pageOffset,
      end: prev.end - pageOffset,
    }));
  };

  const onIconsSearchTermSelect = () => {
    setAppliedSearchTerm(iconsSearchTermDisplayed.toLowerCase().trim());
    setSelectedIconName(null);
    setSliceAmount({ start: 0, end: pageOffset });
  };

  const onIconsSearchInputChange = (e: React.ChangeEvent<TextFieldType>) => {
    const searchTerm = e.target.value;
    if (searchTerm.length) {
      setIconsSearchTermDisplayed(searchTerm);
    } else {
      onClearIconsSearchClick();
    }
  };

  const onClearIconsSearchClick = () => {
    setAppliedSearchTerm('');
    setIconsSearchTermDisplayed('');
    setSelectedIconName(null);
    setSliceAmount({ start: 0, end: pageOffset });
  };

  return (
    <div
      className={cn(
        'border-outline-3 shadow-elevation-4 flex w-[296px] -translate-x-62 translate-y-6 flex-col rounded-2xl border border-solid bg-white p-4',
        className,
      )}
      ref={addIconRef}
    >
      <div className="flex max-w-[296px] flex-wrap items-center justify-between">
        <Typography fontWeight="bold" variant="body1">
          {t('Admin.selectIcon')}
        </Typography>
        <Button
          color="secondary"
          data-testid="AddButton"
          disabled={selectedIconName === null}
          onClick={() => {
            if (selectedIconName) {
              onAddIcon(selectedIconName);
            }
          }}
          size="sm"
        >
          {t('Button.add')}
        </Button>
      </div>
      <div className="my-4">
        <Searchbar
          onChange={onIconsSearchInputChange}
          onClear={onClearIconsSearchClick}
          onSubmit={onIconsSearchTermSelect}
          placeholder={t('Admin.searchForIcons')}
          searchValue={iconsSearchTermDisplayed}
        />
      </div>
      <div className="mb-4 flex h-[182px] w-full flex-row flex-wrap items-center justify-center">
        <>
          {filteredIcons
            ?.slice(sliceAmount.start, sliceAmount.end)
            .map((name, index) => (
              <button
                type="button"
                className={cn(
                  'hover:bg-feedback-success-base m-0.5 size-10 cursor-pointer rounded border-none bg-transparent p-0 transition-colors',
                  name === selectedIconName && 'bg-feedback-success-base',
                )}
                data-testid="Icon"
                key={index}
                onClick={() => onIconClick(name)}
              >
                <img
                  alt={name}
                  className="size-full"
                  src={`${Constants.PATHWAY_CDN_IMG.CATEGORIES}${name}.svg`}
                />
              </button>
            ))}
          {isLoadingIcons && <LoadingIndicator variant="page" />}
        </>
      </div>
      {filteredIcons && filteredIcons.length > pageOffset && (
        <div className="text-brand-navy flex justify-center">
          <IconChevronLeft
            className="mr-2 cursor-pointer"
            onClick={onPrevious}
          />
          <IconChevronRight className="ml-2 cursor-pointer" onClick={onNext} />
        </div>
      )}
    </div>
  );
};

export default AddIcon;
